import React, { useRef } from 'react';
import { Typography } from 'antd';
import { message, QRCode } from 'antd';
import Icon from 'assets/Icons';
import Button from 'components/Button';
import Text from 'components/Text';
import { saveAs } from 'file-saver';
import styled from 'styled-components';
import html2canvas from 'html2canvas';

interface ReferralContainerProps {
  data: {
    linkReferral: string;
  };
}

const ReferralContainer = ({ data }: ReferralContainerProps) => {
  const qrCodeRef = useRef<HTMLDivElement>(null);

  const handleCopyLink = () => {
    if (data?.linkReferral) {
      navigator.clipboard
        .writeText(data?.linkReferral)
        .then(() => {
          message.success('Link copied to clipboard');
        })
        .catch(() => {
          message.error('Failed to copy link');
        });
    }
  };

  const handleDownloadQR = async () => {
    if (!qrCodeRef.current || !data?.linkReferral) {
      message.error('QR Code not available');
      return;
    }

    try {
      const element = qrCodeRef.current;

      const canvas = await html2canvas(element, {
        backgroundColor: '#FFFFFF',
        useCORS: true,
      });

      canvas.toBlob(
        (blob) => {
          if (blob) {
            saveAs(blob, 'referral-qrcode.png');
          } else {
            message.error('Failed to create image');
          }
        },
        'image/png',
        1.0
      );
    } catch (error) {
      console.error('Error downloading QR code:', error);
      message.error('Failed to download QR code');
    }
  };

  return (
    <ReferralContainerStyled>
      <QrCodeBox ref={qrCodeRef}>
        <QRCode value={data?.linkReferral || ''} size={140} className='referral-qr-code' />
      </QrCodeBox>
      <ReferralInfo>
        <Text variant='H7'>Referral Link</Text>
        <ReferralLink>
          <Icon type='link' />
          <LinkBoxStyled>
            <StyledTypography ellipsis={{ tooltip: data?.linkReferral }}>
              {data?.linkReferral}
            </StyledTypography>
          </LinkBoxStyled>

          <button type='button' onClick={handleCopyLink}>
            <Icon type='copy' />
          </button>
        </ReferralLink>
        <Button ghost className='download-qr' onClick={handleDownloadQR}>
          <Text variant='CONTENT_1' color='text_1'>
            Download QR
          </Text>
        </Button>
      </ReferralInfo>
    </ReferralContainerStyled>
  );
};

export default ReferralContainer;

const ReferralContainerStyled = styled.div`
  display: inline-flex;
  background: #fff;
  align-items: center;
  max-height: 275px;
  border-radius: 8px;
  padding: 16px;
  gap: 16px;
  box-shadow: 0px 4px 8px -4px rgba(0, 0, 0, 0.25);
`;
const QrCodeBox = styled.div`
  width: 140px;
  height: 140px;
  padding: 8px;
  background-color: #cbd5e1;

  .referral-qr-code {
    box-shadow: 0px 3.22px 3.22px 0px #00000040;
    background-color: white !important;
  }
`;
const ReferralInfo = styled.div`
  display: grid;
  grid-template-rows: auto auto auto;
  gap: 16px;
  .download-qr {
    width: 160px;
    background-color: #232f3e !important;
  }

  .download-qr:hover {
    background-color: #232f3eb6 !important;
  }
`;
const ReferralLink = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;

  .text-link {
    color: #4384ff !important;
    text-decoration: unset !important;
    cursor: pointer;
  }
`;

const LinkBoxStyled = styled.div`
  max-width: 310px;
`;

const StyledTypography = styled(Typography.Text)`
  color: #1d2129;
  font-family: Poppins;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
`;
